import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Package, ArrowRight } from 'lucide-react';
import { productAPI, BASE_URL } from '../../services/api';

const MegaMenu = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getCategories();
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch products for a category
  const fetchCategoryProducts = async (categoryId) => {
    // Check if already fetched
    if (categoryProducts[categoryId]) return;

    try {
      setProductsLoading(true);
      const response = await productAPI.getProducts({ category_id: categoryId, limit: 8 });
      if (response.data.success) {
        setCategoryProducts(prev => ({
          ...prev,
          [categoryId]: response.data.data?.products || response.data.data || []
        }));
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleMouseEnter = (categoryId) => {
    setActiveCategory(categoryId);
    setIsOpen(true);
    fetchCategoryProducts(categoryId);
  };

  const handleMenuLeave = () => {
    setActiveCategory(null);
    setIsOpen(false);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${BASE_URL}${imageUrl}`;
  };

  const formatPrice = (price) => {
    if (!price) return 'Price on request';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <>
      {/* Category Mega Menu */}
      <div
        style={{
          width: '100%',
          position: 'fixed',
          top: '80px',
          left: 0,
          right: 0,
          zIndex: 45
        }}
        onMouseLeave={handleMenuLeave}
      >
        {/* Menu Bar */}
        <div style={{
          width: '100%',
          backgroundColor: '#2a3143',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 1rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0'
            }}>
              {/* Category Menu Items */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                overflowX: 'auto'
              }}>
                {loading ? (
                  <span style={{ color: 'white', padding: '12px 16px' }}>Loading categories...</span>
                ) : categories.length > 0 ? (
                  categories.map((category) => (
                    <div
                      key={category.id}
                      onMouseEnter={() => handleMouseEnter(category.id)}
                    >
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '12px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap',
                          color: activeCategory === category.id ? 'white' : 'rgba(255,255,255,0.9)',
                          backgroundColor: activeCategory === category.id ? '#1f2532' : 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (activeCategory !== category.id) {
                            e.currentTarget.style.backgroundColor = '#1f2532';
                            e.currentTarget.style.color = 'white';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (activeCategory !== category.id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                          }
                        }}
                      >
                        <span>{category.name}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${
                            activeCategory === category.id ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    </div>
                  ))
                ) : (
                  <span style={{ color: 'white', padding: '12px 16px' }}>No categories</span>
                )}
              </div>

              {/* View All Link */}
              <Link
                to="/products"
                style={{
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#c9a66d',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
              >
                View All Products
              </Link>
            </div>
          </div>
        </div>

        {/* Mega Menu Dropdown with Products */}
        {isOpen && activeCategory && (
          <div
            style={{
              position: 'relative',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
              borderTop: '1px solid #e5e7eb'
            }}
          >
            <div style={{
              maxWidth: '1280px',
              margin: '0 auto',
              padding: '1.5rem 1rem'
            }}>
              {categories
                .filter((cat) => cat.id === activeCategory)
                .map((category) => {
                  const products = categoryProducts[category.id] || [];

                  return (
                    <div key={category.id}>
                      {/* Category Header */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem',
                        paddingBottom: '0.75rem',
                        borderBottom: '2px solid #f3f4f6'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          {category.image_url && (
                            <img
                              src={getImageUrl(category.image_url)}
                              alt={category.name}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '8px',
                                objectFit: 'cover'
                              }}
                            />
                          )}
                          <div>
                            <h3 style={{
                              fontSize: '18px',
                              fontWeight: '700',
                              color: '#111827',
                              margin: 0
                            }}>
                              {category.name}
                            </h3>
                            {category.description && (
                              <p style={{
                                fontSize: '13px',
                                color: '#6b7280',
                                margin: 0
                              }}>
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <Link
                          to={`/products?category=${category.id}`}
                          onClick={() => { setActiveCategory(null); setIsOpen(false); }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#c9a66d',
                            textDecoration: 'none'
                          }}
                        >
                          View All <ArrowRight size={16} />
                        </Link>
                      </div>

                      {/* Products Grid */}
                      {productsLoading && !products.length ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2rem',
                          color: '#6b7280'
                        }}>
                          Loading products...
                        </div>
                      ) : products.length > 0 ? (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: '1rem'
                        }}>
                          {products.slice(0, 8).map((product) => (
                            <Link
                              key={product.id}
                              to={`/products/${product.id}`}
                              onClick={() => { setActiveCategory(null); setIsOpen(false); }}
                              style={{
                                textDecoration: 'none',
                                display: 'block',
                                padding: '0.75rem',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                transition: 'all 0.2s',
                                backgroundColor: 'white'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.borderColor = '#c9a66d';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                              }}>
                                {/* Product Image */}
                                <div style={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '6px',
                                  overflow: 'hidden',
                                  backgroundColor: '#f9fafb',
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {product.image_url ? (
                                    <img
                                      src={getImageUrl(product.image_url)}
                                      alt={product.name}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  ) : (
                                    <Package size={24} color="#9ca3af" />
                                  )}
                                </div>

                                {/* Product Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <h4 style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#111827',
                                    margin: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {product.name}
                                  </h4>
                                  <p style={{
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: '#c9a66d',
                                    margin: '4px 0 0 0'
                                  }}>
                                    {formatPrice(product.price)}
                                    {product.unit && (
                                      <span style={{
                                        fontSize: '11px',
                                        fontWeight: '400',
                                        color: '#6b7280'
                                      }}>
                                        /{product.unit}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2rem',
                          color: '#6b7280'
                        }}>
                          <Package size={32} color="#d1d5db" style={{ marginBottom: '0.5rem' }} />
                          <p style={{ margin: 0 }}>No products in this category</p>
                        </div>
                      )}

                      {/* View More Button */}
                      {products.length > 0 && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '1rem',
                          paddingTop: '1rem',
                          borderTop: '1px solid #f3f4f6'
                        }}>
                          <button
                            onClick={() => {
                              setActiveCategory(null);
                              setIsOpen(false);
                              navigate(`/products?category=${category.id}`);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '10px 24px',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: 'white',
                              backgroundColor: '#c9a66d',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b8956c'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#c9a66d'}
                          >
                            View All {category.name} Products
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MegaMenu;
